import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

type AdminRole = 'admin' | 'moderator' | 'support';

// Role hierarchy: admin > moderator > support
const ROLE_LEVELS: Record<AdminRole, number> = {
  admin: 3,
  moderator: 2,
  support: 1,
};

function hasRequiredRole(userRole: AdminRole | undefined, requiredRole: AdminRole): boolean {
  if (!userRole) return false;
  return ROLE_LEVELS[userRole] >= ROLE_LEVELS[requiredRole];
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Minimum role required to access this route. Defaults to 'support' (any authed admin). */
  requiredRole?: AdminRole;
}

export default function ProtectedRoute({ children, requiredRole = 'support' }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();

  // Not logged in at all
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but insufficient role
  if (!hasRequiredRole(user?.role, requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
